import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import '../ui-pages.css';
import { SkelTable } from '../components/Skeletons';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

// Blue-accented status tones (matching the Orders table look).
const STATUS = {
  pending:       { label: 'Pending',     color: '#8A5A12', bg: '#F5EAD5', dot: '#8A5A12' },
  'in-progress': { label: 'In Progress', color: '#2A5B8A', bg: '#E1ECF5', dot: '#2A5B8A' },
  completed:     { label: 'Completed',   color: '#0F6E56', bg: '#E4F1E9', dot: '#0F6E56' },
};
const st = (s) => STATUS[s] || STATUS.pending;

const Tickets = () => {
  const { workspaceUuid } = useParams();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [workspaceUsers, setWorkspaceUsers] = useState([]);
  const [filter, setFilter] = useState('all');
  const [deletedItems, setDeletedItems] = useState([]);

  useEffect(() => {
    fetchTickets();
    fetchWorkspaceUsers();
    const interval = setInterval(fetchTickets, 10000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceUuid]);

  // Opening the page clears the unread badge (server-side) + updates sidebars live.
  useEffect(() => {
    const t = setTimeout(() => {
      axios.patch(`${API_URL}/api/tickets/mark-all-read/all`, { workspaceUuid }).catch(() => {});
    }, 700);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceUuid]);

  const fetchWorkspaceUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/users/workspace-users`, { headers: { Authorization: `Bearer ${token}` } });
      setWorkspaceUsers(res.data || []);
    } catch (err) { console.error(err); }
  };

  const fetchTickets = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/tickets?workspaceUuid=${workspaceUuid}`);
      setTickets(res.data.tickets || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const markAsRead = async (id) => {
    try {
      setTickets(prev => prev.map(t => t._id === id ? { ...t, isRead: true } : t));
      await axios.patch(`${API_URL}/api/tickets/${id}`, { isRead: true });
    } catch (err) { console.error(err); fetchTickets(); }
  };

  const updateStatus = async (id, status) => {
    try {
      setTickets(prev => prev.map(t => t._id === id ? { ...t, status } : t));
      await axios.patch(`${API_URL}/api/tickets/${id}`, { status });
    } catch (err) { console.error(err); fetchTickets(); }
  };

  const updateAssignee = async (id, assignedTo) => {
    try {
      setTickets(prev => prev.map(t => t._id === id ? { ...t, assignedTo } : t));
      await axios.patch(`${API_URL}/api/tickets/${id}`, { assignedTo });
    } catch (err) { console.error(err); fetchTickets(); }
  };

  const handleOpenChat = (e, ticket) => {
    e.stopPropagation();
    markAsRead(ticket._id);
    if (ticket.associatedPhone) {
      const platform = ticket.platform || 'whatsapp';
      window.open(`/workspace/${workspaceUuid}/chats/${platform}/${encodeURIComponent(ticket.associatedPhone)}`, '_blank', 'noopener,noreferrer');
    }
  };

  const fetchDeleted = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/tickets?workspaceUuid=${workspaceUuid}&deleted=true`);
      setDeletedItems(res.data.tickets || []);
    } catch (err) { console.error(err); }
  };

  const handleDelete = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm('Delete this ticket? It will be moved to the Deleted tab.')) return;
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    setTickets(prev => prev.filter(t => t._id !== id));
    try { await axios.delete(`${API_URL}/api/tickets/${id}`, { data: { deletedBy: user.email, deletedByName: user.name } }); }
    catch (err) { console.error(err); fetchTickets(); }
  };

  const selectTab = (k) => { setFilter(k); if (k === 'deleted') fetchDeleted(); };

  const when = (d) => d ? new Date(d).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

  const counts = {
    all: tickets.length,
    pending: tickets.filter(t => t.status === 'pending').length,
    'in-progress': tickets.filter(t => t.status === 'in-progress').length,
    completed: tickets.filter(t => t.status === 'completed').length,
  };
  const isDeletedTab = filter === 'deleted';
  const shown = isDeletedTab ? deletedItems : (filter === 'all' ? tickets : tickets.filter(t => t.status === filter));

  const tabs = [
    { k: 'all', label: 'All' }, { k: 'pending', label: 'Pending' },
    { k: 'in-progress', label: 'In Progress' }, { k: 'completed', label: 'Completed' },
  ];

  return (
    <div className="ui-page">
      <style>{css}</style>
      <header className="ui-head">
        <div>
          <h1 className="ui-title">Support Tickets</h1>
          <p className="ui-sub">Customer requests and internal tasks, raised manually or by automation.</p>
        </div>
      </header>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 18, flexWrap: 'wrap' }}>
        <div className="tk-tabs" style={{ marginBottom: 0 }}>
          {tabs.map(t => (
            <button key={t.k} className={`tk-tab ${filter === t.k ? 'on' : ''}`} onClick={() => selectTab(t.k)}>
              {t.label}<span className="tk-tab-n">{counts[t.k]}</span>
            </button>
          ))}
        </div>
        <button onClick={() => selectTab('deleted')}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 7, padding: '9px 15px', borderRadius: 999, fontWeight: 800, fontSize: 13, cursor: 'pointer', border: '1px solid #B23A3A', background: isDeletedTab ? '#F6E4E1' : '#fff', color: '#B23A3A' }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m2 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" /></svg>
          Deleted{isDeletedTab ? ` (${deletedItems.length})` : ''}
        </button>
      </div>

      {loading ? (
        <SkelTable rows={8} cols={5} />
      ) : shown.length === 0 ? (
        <div className="ui-empty">
          <div className="ui-empty-ic"><svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M3 13h4l2 3h6l2-3h4" /><path d="M5 5h14l2 8v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4z" /></svg></div>
          <h2>{filter === 'all' ? 'No tickets yet' : `No ${st(filter).label.toLowerCase()} tickets`}</h2>
          <p>Tickets created manually or via workflow automation will appear here.</p>
        </div>
      ) : (
        <div className="tk-table-card">
          <div className="tk-scroll">
            <table className="tk-table">
              <thead>
                <tr>
                  <th>Ticket</th><th>Status</th><th>Assignee</th><th>Raised</th><th></th>
                </tr>
              </thead>
              <tbody>
                {shown.map((t) => {
                  const s = st(t.status);
                  return (
                    <tr key={t._id} className={t.isRead ? '' : 'unread'} onClick={() => !t.isRead && markAsRead(t._id)}>
                      <td>
                        <div className="tk-cell">
                          {!t.isRead && <span className="tk-newdot" />}
                          <div style={{ minWidth: 0 }}>
                            <div className="tk-cat">{t.category}</div>
                            <div className="tk-query">{t.query}</div>
                          </div>
                        </div>
                      </td>
                      <td onClick={(e) => e.stopPropagation()}>
                        <div className="tk-status" style={{ color: s.color, background: s.bg, boxShadow: `inset 0 0 0 1px ${s.bg}` }}>
                          <span className="tk-status-dot" style={{ background: s.dot }} />
                          <select value={t.status} onChange={(e) => updateStatus(t._id, e.target.value)} style={{ color: s.color }}>
                            <option value="pending">Pending</option>
                            <option value="in-progress">In Progress</option>
                            <option value="completed">Completed</option>
                          </select>
                          <svg className="tk-chev" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ color: s.color }}><path d="m6 9 6 6 6-6" /></svg>
                        </div>
                      </td>
                      <td onClick={(e) => e.stopPropagation()}>
                        <select className="tk-assign" value={t.assignedTo || ''} onChange={(e) => updateAssignee(t._id, e.target.value)}>
                          <option value="">Unassigned</option>
                          {workspaceUsers.map(u => <option key={u._id} value={u.name || u.email}>{u.name || u.email}</option>)}
                        </select>
                      </td>
                      <td className="tk-raised">
                        <div className="tk-by">{t.createdBy || '—'}</div>
                        <div className="tk-muted">{when(t.createdAt)}</div>
                      </td>
                      <td className="tk-right" onClick={(e) => e.stopPropagation()}>
                        {isDeletedTab ? (
                          <span style={{ fontSize: 11.5, color: '#9C978C', fontWeight: 600 }}>
                            Deleted by <b style={{ color: '#6B6862' }}>{t.deletedByName || t.deletedBy || 'Unknown'}</b>{t.deletedAt ? ` · ${when(t.deletedAt)}` : ''}
                          </span>
                        ) : (
                          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, justifyContent: 'flex-end' }}>
                            {t.associatedPhone && (
                              <button className="tk-open" onClick={(e) => handleOpenChat(e, t)}>
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg>
                                Open chat
                              </button>
                            )}
                            <button title="Delete" onClick={(e) => handleDelete(e, t._id)}
                              style={{ display: 'inline-grid', placeItems: 'center', width: 30, height: 30, background: '#fff', border: '1px solid #B23A3A', color: '#B23A3A', borderRadius: 999, cursor: 'pointer' }}>
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m2 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 5v6m4-6v6" /></svg>
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

const css = `
.tk-tabs{ display:flex; gap:8px; margin-bottom:18px; max-width:100%; flex-wrap:wrap; align-items:center; }
.tk-tab{ background:#fff; border:1px solid #E7E1D6; cursor:pointer; font-size:13px; font-weight:500; color:#3A3A37; padding:8px 15px; border-radius:999px; display:flex; align-items:center; gap:8px; white-space:nowrap; transition:color .15s, background .15s, border-color .15s; }
.tk-tab:hover{ color:#111111; background:#FAF7F1; }
.tk-tab.on{ background:#111111; color:#F9F4EE; border-color:#111111; box-shadow:none; }
.tk-tab-n{ font-size:11px; font-weight:500; font-family:'IBM Plex Mono',monospace; background:#FAF7F1; color:#9C978C; border-radius:999px; padding:1px 8px; }
.tk-tab.on .tk-tab-n{ background:rgba(249,244,238,0.2); color:#F9F4EE; }

.tk-table-card{ background:#fff; border:1px solid #E7E1D6; border-radius:14px; box-shadow:none; overflow:hidden; }
.tk-scroll{ overflow-x:auto; }
.tk-table{ width:100%; border-collapse:collapse; min-width:820px; }
.tk-table thead th{ text-align:left; font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:#9C978C; padding:13px 18px; background:#FAF7F1; border-bottom:1px solid #E7E1D6; white-space:nowrap; }
.tk-table tbody td{ padding:14px 18px; border-bottom:1px solid #E7E1D6; font-size:13.5px; color:#3A3A37; vertical-align:middle; }
.tk-table tbody tr:last-child td{ border-bottom:none; }
.tk-table tbody tr{ transition:background .12s; }
.tk-table tbody tr:hover{ background:#FAF7F1; }
.tk-table tbody tr.unread{ background:#FAF7F1; cursor:pointer; }
.tk-table tbody tr.unread:hover{ background:#F1ECE2; }
.tk-right{ text-align:right; white-space:nowrap; }
.tk-muted{ color:#9C978C; white-space:nowrap; font-size:12px; font-family:'IBM Plex Mono',monospace; }

.tk-cell{ display:flex; align-items:flex-start; gap:10px; }
.tk-newdot{ width:8px; height:8px; border-radius:50%; background:#111111; box-shadow:0 0 0 3px rgba(17,17,17,.12); flex:none; margin-top:5px; }
.tk-cat{ font-size:14px; font-weight:750; color:#111111; letter-spacing:-0.01em; }
.tk-query{ font-size:13px; color:#6B6862; line-height:1.45; margin-top:2px; max-width:560px; display:-webkit-box; -webkit-line-clamp:2; -webkit-box-orient:vertical; overflow:hidden; }

.tk-status{ display:inline-flex; align-items:center; gap:7px; border-radius:999px; padding:5px 8px 5px 11px; }
.tk-status-dot{ width:7px; height:7px; border-radius:50%; }
.tk-status select{ border:none; background:none; font-size:12.5px; font-weight:750; outline:none; cursor:pointer; appearance:none; padding:0; }
.tk-chev{ opacity:.75; pointer-events:none; }

.tk-assign{ background:#fff; border:1px solid #E7E1D6; padding:6px 10px; border-radius:999px; font-size:12.5px; font-weight:700; color:#3A3A37; outline:none; cursor:pointer; transition:border-color .15s; max-width:160px; }
.tk-assign:hover{ border-color:#9C978C; }

.tk-raised .tk-by{ font-weight:700; color:#3A3A37; font-size:12.5px; }

.tk-open{ display:inline-flex; align-items:center; gap:6px; background:#FFFFFF; color:#3A3A37; border:1px solid #E7E1D6; padding:6px 11px; border-radius:999px; font-weight:700; font-size:12px; cursor:pointer; white-space:nowrap; transition:background .15s; }
.tk-open:hover{ background:#111111; color:#F9F4EE; border-color:#111111; }
`;

export default Tickets;
